<template>
  <div
    v-click-outside="hideSearchResults"
    class="search-container relative-parent"
    @keyup.esc="hideSearchResults"
  >
    <input
      v-model.trim="query"
      type="search"
      :class="['search-field field', { 'search-field--open': !!query }]"
      :placeholder="$t('search.placeholder')"
    />
    <GrowTransition>
      <Box
        v-if="searchResultsPanelVisible"
        variant="default"
        size="tight"
        class="search-results box--floating"
      >
        <div v-if="loading" class="pdg">
          <Loader />
        </div>
        <SearchResults
          v-else-if="hasResults"
          :query="query"
          :results="results"
        />
        <div v-else class="pdg text-muted" data-test="no-results-message">
          {{ $t('search.no-results') }}
        </div>
      </Box>
    </GrowTransition>
  </div>
</template>

<script>
import axios from 'axios';
import _ from 'lodash';
import { mapMutations, mapState } from 'vuex';

import { search } from '@/services/search';

import GrowTransition from '@/components/transitions/Grow';
import Loader from '@/components/ui/Loader';
import SearchResults from '@/components/search/SearchResults';

export default {
  components: {
    GrowTransition,
    SearchResults,
    Loader,
  },

  data() {
    return {
      query: '',
      results: {},
      loading: false,
      cancelCurrentRequest: null,
    };
  },

  computed: {
    ...mapState('ui', ['searchResultsPanelVisible']),

    hasResults() {
      return (
        this.results &&
        ((this.results.vehicles && this.results.vehicles.items.length) ||
          (this.results.contacts && this.results.contacts.items.length) ||
          (this.results.lines && this.results.lines.items.length) ||
          (this.results.groups && this.results.groups.items.length))
      );
    },
  },

  watch: {
    query() {
      // Abort any on-going search request
      if (this.cancelCurrentRequest) {
        this.cancelCurrentRequest();
      }

      if (this.query.length > 0) {
        this.setSearchResultsPanelVisibility(true);
        this.loading = true;
        this.debouncedSearch();
      } else {
        this.setSearchResultsPanelVisibility(false);
      }
    },
  },

  created() {
    this.debouncedSearch = _.debounce(this.search, 300);
  },

  methods: {
    ...mapMutations('users', ['setUsers']),
    ...mapMutations('ui', ['setSearchResultsPanelVisibility']),

    search() {
      search(this.query, {
        cancelToken: new axios.CancelToken(c => {
          this.cancelCurrentRequest = c;
        }),
      }).then(
        response => {
          this.results = response.data;
          this.loading = false;
          this.cancelCurrentRequest = null;
          let users = [];

          if (response.data.vehicles && response.data.vehicles.items) {
            users = [...users, ...response.data.vehicles.items];
          }

          if (response.data.contacts && response.data.contacts.items) {
            users = [...users, ...response.data.contacts.items];
          }

          this.setUsers(users);
        },
        error => {
          if (axios.isCancel(error)) {
            // Request was cancelled by a new search
            // Skip the traditional error handling
            return;
          }

          this.loading = false;
          this.setSearchResultsPanelVisibility(false);
          this.$notify({
            type: 'error',
            title: this.$t('search.label'),
            text: error.message,
            duration: -1,
          });
        }
      );
    },

    hideSearchResults() {
      this.query = '';
    },
  },
};
</script>

<style lang="scss" scoped>
.search-container {
  max-width: 700px;
}

.search-field {
  padding-left: 42px;

  color: white;
  font-size: rem(18px);

  background: $alt-500 url('~@/assets/images/search.svg') no-repeat 8px 50%;
  border: none;

  &::placeholder {
    color: $alt-300;
    opacity: 1;
  }
}

.search-field--open {
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
}

.search-results {
  width: 100%;

  position: absolute;
  top: 100%;

  border-bottom-right-radius: $border-radius-small;
  border-bottom-left-radius: $border-radius-small;
}
</style>
